/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { Children, type ForwardedRef, createContext, forwardRef, isValidElement } from 'react';
import { type ContextValue, OverlayArrow as OverlayArrowBase, Provider, Tooltip as TooltipCore, type TooltipProps as TooltipCoreProps, useContextProps } from 'react-aria-components';
import { zIndices } from '../../constants.stylex';
import { NotificationErrorOutlineFillIcon } from '../../icons/notification-error-outline-fill/notification-error-outline-fill.icon';
import { $ } from '../../tokens.stylex';
import { Text, TextContext } from '../text/text.component';

interface TooltipProps extends Omit<TooltipCoreProps, 'style' | 'className'> {
  /** Whether the tooltip is attentive. (Default: false) */
  isAttentive?: boolean;
  style?: stylex.StyleXStyles;
}

const TooltipContext = createContext<ContextValue<TooltipProps, HTMLDivElement>>({});

const Tooltip = forwardRef((props: TooltipProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, TooltipContext);

  const { style, isAttentive = false, placement = 'top', ...coreProps } = props;

  return (
    <TooltipCore
      ref={ref}
      {...stylex.props(
        styles.base,
        positionVariants.base,
        isAttentive && styles.attentive,
        style)}
      placement={placement}
      {...coreProps}>
      {(renderProps) => {
        const resolvedChildren = typeof coreProps.children === 'function' ? coreProps.children(renderProps) : coreProps.children;

        const isTextOnly = Children.toArray(resolvedChildren).every(c => !isValidElement(c));

        return (
          <Provider values={[
            [TextContext, {
              slot: null!,
              color: isAttentive ? 'critical' : 'inverted',
              size: 14
            }],
          ]}>
            <OverlayArrowBase {...stylex.props(arrowStyles.base)}>
              <svg
                data-placement={renderProps.placement}
                viewBox="0 0 13 6"
                fill="none"
                {...stylex.props(
                  iconStyles.base,
                  isAttentive && iconStyles.attentive,
                  iconPositionStyles.base,
                )}>
                <path d="M7.56066 5.50991C6.97487 6.16336 6.02513 6.16336 5.43934 5.50991L0.499999 1.04907e-06L12.5 0L7.56066 5.50991Z" fillOpacity="0.96" />
              </svg>
            </OverlayArrowBase>
            {isAttentive && <NotificationErrorOutlineFillIcon color="critical" size={12} />}
            {isTextOnly ? <Text>{resolvedChildren}</Text> : resolvedChildren}
          </Provider>
        );
      }}
    </TooltipCore>
  );
});

const styles = stylex.create({
  base: {
    borderRadius: 6,
    gap: 6,
    outline: 'none',
    paddingBlock: 6,
    paddingInline: 12,
    alignItems: 'center',
    backdropFilter: 'blur(12px)',
    backgroundColor: $.opacity96,
    boxSizing: 'border-box',
    display: 'inline-flex',
    flexDirection: 'row',
    forcedColorAdjust: 'none',
    justifyContent: 'center',
    opacity: {
      default: 1,
      ':is([data-entering], [data-exiting])': 0,
    },
    transitionDuration: '200ms',
    transitionProperty: 'transform, opacity',
    zIndex: zIndices.tooltip,
    minHeight: 30,
  },
  attentive: { backgroundColor: $.surfaceCriticalMedium },
});

const positionVariants = stylex.create({
  base: {
    marginBottom: {
      default: 0,
      ':is([data-placement="top"])': 8,
    },
    marginTop: {
      default: 0,
      ':is([data-placement="bottom"])': 8,
    },
    marginRight: {
      default: 0,
      ':is([data-placement="left"])': 8,
    },
    marginLeft: {
      default: 0,
      ':is([data-placement="right"])': 8,
    },
    transform: {
      default: 'translate3d(0, 0, 0)',
      ':is([data-placement="top"][data-entering], [data-placement="top"][data-exiting])': 'translateY(4px)',
      ':is([data-placement="bottom"][data-entering], [data-placement="bottom"][data-exiting])': 'translateY(-4px)',
      ':is([data-placement="left"][data-entering], [data-placement="left"][data-exiting])': 'translateX(4px)',
      ':is([data-placement="right"][data-entering], [data-placement="right"][data-exiting])': 'translateX(-4px)',
    },
  },
});

const arrowStyles = stylex.create({
  base: {
    display: 'flex',
    height: 6,
    width: 13,
    marginLeft: {
      default: 0,
      ':is([data-placement="left"])': -4,
    },
    marginRight: {
      default: 0,
      ':is([data-placement="right"])': -4,
    },
  },
});

const iconStyles = stylex.create({
  base: { fill: $.opacity96 },
  attentive: { fill: $.surfaceCriticalMedium },
});

const iconPositionStyles = stylex.create({
  base: {
    transform: {
      default: 'rotate(0deg)',
      ':is([data-placement="bottom"])': 'rotate(180deg)',
      ':is([data-placement="left"])': 'rotate(-90deg)',
      ':is([data-placement="right"])': 'rotate(90deg)',
    },
  },
});

export { Tooltip, TooltipContext, type TooltipProps };
